import { Link } from "@mui/material";

const EightWays = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="text--post_paragraph">
            Learning new things can be tricky but the value of personal &
            professional growth & development is immense. Like anything,
            learning takes time, and we only have so many hours in a day. So how
            can you make the most out of your limited time by speeding up the
            learning process? Thanks to numerous studies, neuroscience & years
            of experience, we now have a better understanding of how we learn
            and the most effective ways our brains process and retain
            information.
          </p>
          <p className="text--post_paragraph">
            Here are 8 proven ways you can start learning faster today.
          </p>
          <h2 className="header header--page_title_h2_left">
            1. Take Written Notes
          </h2>
          <p className="text--post_paragraph">
            Even if it might seem that typing your notes on a laptop during a
            conference or lecture may be a more efficient way of note taking, it
            actually doesn’t help you learn the information any faster. To speed
            up your learning, start taking notes the old-fashioned way, by
            writing them. According to NPR, research has shown that people who
            type their notes retain the information at a lower level (
            <Link
              href="https://www.npr.org/2016/04/17/474525392/attention-students-put-your-laptops-away"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about the effectiveness of hand-written notes over typed notes on NPR"
            >
              NPR
            </Link>
            ). Students who take notes by hand, learn more efficiently.
          </p>
          <p className="text--post_paragraph">
            The extra information processing involved in writing out the
            information, improves comprehension and retention (
            <Link
              href="https://www.npr.org/2016/04/17/474525392/attention-students-put-your-laptops-away"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              NPR
            </Link>
            ). Reframing the information in your own words helps you retain the
            information longer, so consequently, you’ll have a better recall.
          </p>
          <h2 className="header header--page_title_h2_left">
            2. Use Distributed Practice
          </h2>
          <p className="text--post_paragraph">
            This tip involves distributing multiple practices (group study) on a
            topic during a period of time. A wealth of research displays that,
            “to achieve long-lasting retention, information must be practiced
            and/or tested repeatedly, with repeated practice well distributed
            over time” (
            <Link
              href="https://www.sciencedirect.com/science/article/abs/pii/S2211949315000022"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about distributed practice in learning from the research by Gerbier, E. & Toppino, T."
            >
              Gerbier, E. & Toppino, T.
            </Link>
            ) The first step is to take detailed notes while the topic is being
            discussed. Afterwards, take a couple minutes to look over your
            notes, making any additions to add details and ensure accuracy. Do
            this a couple of times following each class or period of
            instruction.
          </p>
          <p className="text--post_paragraph">
            With time, you can begin to spread the sessions out, starting with
            once per day and eventually up to three sessions a day. It has been
            proven that “spaced or distributed practice is superior to massed
            practice for long-term learning and retention” (
            <Link
              href="https://digitalpromise.org/2019/05/08/ask-the-cognitive-scientist-distributed-practice/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              Kang, S.
            </Link>
            ). Spacing out practice is also typically easier to do because small
            study sessions will keep you motivated to continue learning.
          </p>
          <h2 className="header header--page_title_h2_left">
            3. Get Good Rest
          </h2>
          <p className="text--post_paragraph">
            Let’s imagine that you have a big project or a major presentation
            tomorrow and you are not prepared. Like many of us have done before,
            you will stay up too late trying to cram as much as possible.
            However, exhausting yourself while trying to study or prepare for
            something is not the most efficient way for our brains to retain
            information.
          </p>
          <p className="text--post_paragraph">
            Getting good rest is beneficial for learning for two reasons. For
            one, “a sleep-deprived person cannot focus attention optimally and
            therefore cannot learn efficiently”. Secondly, “sleep itself has a
            role in the consolidation of memory, which is essential for learning
            new information” (
            <Link
              href="http://healthysleep.med.harvard.edu/healthy/matters/benefits-of-sleep/learning-memory#:~:text=First%2C%20a%20sleep%2Ddeprived%20person,and%20therefore%20cannot%20learn%20efficiently.&text=Acquisition%20and%20recall%20occur%20only,connections%20that%20form%20our%20memories."
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Learn about the importance of sleep in learning and memory from the Division of Sleep Medicine at Harvard Medical School"
            >
              Division of Sleep Medicine at Harvard Medical School
            </Link>
            ). Deep sleep can also strengthen memories if the sleep occurs
            within 12 hours of learning the new information.
          </p>
          <h2 className="header header--page_title_h2_left">
            4. Use Mnemonic Devices
          </h2>
          <p className="text--post_paragraph">
            One of the best ways to memorize a lot of information fast is to use
            a mnemonic device: a pattern of letters, sounds or other
            associations that assist in learning something. Mnemonics work by
            “associating easy-to-remember clues with complex or unfamiliar data”
            (
            <Link
              href="https://www.thoughtco.com/mnemonic-devices-tools-7755"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read more about mnemonic devices as learning tools on ThoughtCo"
            >
              Kelly, M.
            </Link>
            ). One of the most popular mnemonic devices is one most everyone
            learned in kindergarten: the alphabet song. This song helps children
            remember their “ABCs,” and it remains in their memory into
            adulthood.
          </p>
          <p className="text--post_paragraph">
            Mnemonics help you summarize, simplify and compress information to
            make it easier to learn. It can be really helpful for students
            learning new material, people studying a new language or someone
            learning large amounts of new information. To conclude, if you need
            to memorize and store large amounts of new information, you should
            try a mnemonic device and you will find it is easier to remember the
            information long past the test you were studying for.
          </p>
          <h2 className="header header--page_title_h2_left">
            5. Take a Brain Break
          </h2>
          <p className="text--post_paragraph">
            Over-studying is a real thing. In order to learn something new, our
            brains must send signals to our receptors to save the new
            information, however, stress and overexhaustion would prevent your
            brain from effectively storing information.
          </p>
          <p className="text--post_paragraph">
            When we are anxious or feeling overwhelmed, our brains can
            effectively shut down. The best way to combat this is by taking a
            “brain break,” or simply switching your activity to focus on
            something else. It refreshes our thinking and helps us discover
            another solution to a problem or see a situation through a different
            lens. Even just a five-minute break can relieve brain fatigue and
            help you refocus.
          </p>
          <h2 className="header header--page_title_h2_left">
            6. Stay Hydrated
          </h2>
          <p className="text--post_paragraph">
            Staying hydrated is crucial to physical & mental health & wellness.
            Staying hydrated is very important to achieve optimal cognitive
            abilities. Studies have shown that “once thirst is felt, mental
            performance including memory, attention and concentration decreases
            by as much as 10%” (
            <Link
              href="https://patch.com/virginia/ashburn/bp--dehydration-its-impact-on-learning"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about the impact of dehydration on learning and cognitive performance on Patch"
            >
              Hoaglund, S.
            </Link>
            ). Philippa Norman M.D. backs this claim by pointing out: “Water is
            essential for optimal brain health and function. Water is necessary
            to maintain the tone of membranes for normal neurotransmission” (
            <Link
              href="https://patch.com/virginia/ashburn/bp--dehydration-its-impact-on-learning"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
            >
              Norman, P.
            </Link>
            ). Dehydration can seriously affect our mental function. When you
            don’t drink water, your brain has to work harder than usual.
          </p>
          <h2 className="header header--page_title_h2_left">
            7. Learn Information in Multiple Ways
          </h2>
          <p className="text--post_paragraph">
            When you use multiple techniques to learn something, you will use a
            range of areas in your brain to process & store information about
            the subject. This allows the information to become more
            interconnected in your brain. It creates a redundancy of knowledge
            in your brain, helping you fully comprehend & ingrain the
            information and not just memorize it.
          </p>
          <p className="text--post_paragraph">
            You can do this by using different media, such as taking notes,
            reading a textbook, using flashcards, watching a video and/or
            listening to a podcast on the topic. The more resources you will
            utilize, the faster you will learn.
          </p>
          <h2 className="header header--page_title_h2_left">
            8. Make Connections
          </h2>
          <p className="text--post_paragraph">
            The more you can relate new concepts to things that you already
            understand, the faster you will learn the new information. According
            to the book{" "}
            <Link
              href="https://www.retrievalpractice.org/make-it-stick"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Learn more about effective learning techniques from the book 'Make It Stick' on Retrieval Practice"
            >
              Make It Stick
            </Link>
            , many common study habits are counterproductive. It might create an
            illusion of mastery, however the information quickly fades from our
            minds.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default EightWays;
